<template>
  <div class="fixed inset-0 flex flex-col bg-base-200 text-base-content">
    <!-- Status bar -->
    <header class="flex items-center justify-between gap-3 px-4 pt-[calc(env(safe-area-inset-top)+8px)] pb-2 bg-base-200 border-b border-base-300">
      <div class="flex items-baseline gap-2 min-w-0">
        <span class="font-bold text-sm tracking-tight shrink-0">BVRT</span>
        <span class="text-[11px] text-neutral hidden sm:inline shrink-0">Yarraman → Wulkuraka</span>
      </div>
      <div class="text-xs text-right truncate">{{ statusText }}</div>
    </header>

    <div class="flex-1 min-h-0 flex flex-col lg:flex-row">
    <!-- Map -->
    <div class="relative flex-1 min-h-0">
      <div ref="mapEl" class="w-full h-full"></div>

      <!-- Tap-outside catcher for the phone legend -->
      <div v-if="legendOpen" class="lg:hidden absolute inset-0 z-10" @click="legendOpen = false"></div>

      <!-- Legend — a popup above the bottom controls on phones, always shown from lg up -->
      <div
        class="absolute top-3 left-3 z-20 bg-base-100/95 rounded-xl shadow-md px-3 py-2 text-[11px] leading-[1.7] pointer-events-none
               max-lg:top-auto max-lg:bottom-20 max-lg:shadow-lg"
        :class="legendOpen ? 'block' : 'hidden lg:block'">
        <div class="flex items-center gap-2">
          <span class="legend-line" style="background:#1d4ed8"></span>{{ currentDay.label }}'s leg
        </div>
        <div class="flex items-center gap-2"><span class="legend-dot" style="background:#2f6b4f"></span>Trailhead</div>
        <div class="flex items-center gap-2"><span class="legend-dot" style="background:#b3541e"></span>Pub</div>
        <div class="flex items-center gap-2"><span class="legend-dot" style="background:#6b6559"></span>Old railway park</div>
        <div class="flex items-center gap-2">
          <svg width="12" height="12" viewBox="0 0 12 12" class="shrink-0"><path d="M6 1 L11 10.5 L1 10.5 Z" fill="#d81b60" stroke="#fff" stroke-width="1.4"/></svg>You
        </div>
        <div class="flex items-center gap-2">
          <span class="legend-camp shrink-0">
            <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="#fff" stroke-width="2.6"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V20h13V9.5"/>
            </svg>
          </span>Base camp
        </div>
      </div>

      <!-- Bottom-left controls — phones only; on desktop the list and legend are always shown -->
      <div class="lg:hidden absolute bottom-4 left-3 z-20 flex items-center gap-2">
        <button
          class="h-12 pl-3 pr-4 rounded-full bg-base-100 shadow-lg flex items-center gap-2 text-[13px] font-semibold active:scale-95 transition-transform"
          aria-label="Show stops and distances" @click="sheetOpen = true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4c736f" stroke-width="2" stroke-linecap="round">
            <path d="M4 6h16M4 12h16M4 18h16"/>
          </svg>
          Stops
          <span class="text-[11px] font-normal text-neutral">{{ currentDay.label }}</span>
        </button>

        <button
          class="w-12 h-12 rounded-full bg-base-100 shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Show map key" @click="legendOpen = !legendOpen">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4c736f" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><circle cx="12" cy="7.6" r="1.1" fill="#4c736f" stroke="none"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Scrim behind the phone sheet -->
    <div v-if="sheetOpen" class="lg:hidden fixed inset-0 z-20 bg-[rgba(61,52,40,0.35)]" @click="sheetOpen = false"></div>

    <!-- Stops: an overlay sheet on phones, a permanent sidebar from lg up -->
    <section
      class="bg-base-100 flex flex-col
             fixed inset-x-0 bottom-0 z-30 max-h-[62vh] rounded-t-2xl shadow-[0_-4px_20px_rgba(61,52,40,0.25)]
             lg:static lg:z-auto lg:max-h-none lg:w-[380px] lg:shrink-0 lg:rounded-none lg:shadow-none lg:border-l lg:border-base-300"
      :class="sheetOpen ? 'flex' : 'hidden lg:flex'">
      <header class="flex items-center justify-between px-4 pt-3 pb-2">
        <h2 class="font-semibold text-sm">Stops &amp; Distances</h2>
        <div class="flex items-center gap-3">
          <a href="https://agentaconsulting.com" target="_blank" rel="noopener"
             class="flex items-center gap-1.5 text-[10px] text-neutral">
            made by <AgentaMark class="h-[13px] w-auto" />
          </a>
          <button class="lg:hidden -mr-1 w-7 h-7 rounded-full text-neutral text-lg leading-none"
                  aria-label="Close" @click="sheetOpen = false">✕</button>
        </div>
      </header>

      <div class="flex items-center gap-2.5 px-4 pb-1 text-[9.5px] uppercase tracking-wide text-neutral">
        <span class="ml-auto w-16 text-right">Total</span>
        <span class="w-14 text-right">Leg</span>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto px-4 pb-1">
        <div v-for="stop in currentDay.stops" :key="stop.town"
             class="flex items-center gap-2.5 py-[7px] border-b border-base-200 last:border-0">
          <span class="legend-dot shrink-0" :style="{ background: roleColor(stop.role) }"></span>
          <span class="text-[13px] font-medium">{{ stop.town }}</span>
          <span v-if="roleLabel(stop.role)" class="text-[10.5px] uppercase tracking-wide"
                :style="{ color: roleColor(stop.role) }">{{ roleLabel(stop.role) }}</span>
          <span class="ml-auto w-16 text-right text-xs tabular-nums">{{ stop.km.toFixed(1) }} km</span>
          <span class="w-14 text-right text-xs tabular-nums text-neutral">{{ stop.legKm.toFixed(1) }} km</span>
        </div>
      </div>

      <!-- Day tabs, pinned at the very bottom -->
      <nav class="grid grid-cols-3 gap-1 p-2 pb-[calc(env(safe-area-inset-bottom)+8px)] bg-base-100 border-t border-base-200">
        <button v-for="(day, i) in days" :key="day.label"
                class="py-2 rounded-lg text-[13px] font-semibold transition-colors"
                :class="i === selectedDay ? 'bg-primary text-primary-content' : 'bg-base-200 text-neutral'"
                @click="selectedDay = i">
          {{ day.label }}
        </button>
      </nav>
    </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { createConsumer } from "@rails/actioncable"
import AgentaMark from "./AgentaMark.vue"
import townsData from "../data/towns.json"
import trailData from "../data/trail_line.json"
import itineraryData from "../data/itinerary.json"
import elevationData from "../data/elevation_profile.json"

const MAPBOX_TOKEN = "pk.eyJ1Ijoia2VpdGhyb3dlbGwiLCJhIjoiY21yc29jbGttMTFjcDRqcTB5YmZpbjJjdSJ9.B6WYFDwg1pb7JInJY2bIaA"
const TRAIL_COLOR = "#9c5a22" // the trail's rust, used for every day not selected
const SELECTED_DAY_COLOR = "#1d4ed8"
// The live position must never be mistaken for a trail leg, and it sits directly on
// top of one, so it gets a hue nothing else on the map uses: no magenta appears in
// the terrain, the rust trail, the town dots or the base camp pin.
const POSITION_COLOR = "#d81b60"
const TOTAL_KM = townsData.total_km
const TRIP_START_MS = Date.parse("2026-07-24T08:30:00+10:00")

const TYPE_COLORS = {
  trailhead: "#2f6b4f",
  "trailhead+pub": "#2f6b4f",
  pub: "#b3541e",
  park: "#6b6559",
  park_old_railway_stop: "#6b6559",
}

// Stops in riding order (Yarraman first).
const stops = [...townsData.stops].sort((a, b) => a.km_from_yarraman - b.km_from_yarraman)
const townByName = Object.fromEntries(stops.map((s) => [s.town, s]))

// Normalise the two day shapes into { label, date, endTown, stops: [...] }. `km` is the
// running total from Yarraman; `legKm` is the hop from the previous stop, so the first
// stop of a day — the one you roll out from — is 0 for both.
const days = itineraryData.days.map((d) => ({
  label: d.label,
  date: d.date,
  endTown: d.end_town,
  stops: (d.route_order_km_from_yarraman || d.route_order).map((r, i, all) => {
    const km = r.km ?? r.km_from_yarraman
    const prev = i === 0 ? km : (all[i - 1].km ?? all[i - 1].km_from_yarraman)
    return { town: r.town, km, legKm: km - prev, role: r.role }
  }),
}))

const mapEl = ref(null)
const selectedDay = ref(defaultDay())
const position = ref(null) // latest server record, or null
const sheetOpen = ref(false) // phone-only: the stops sheet overlays the map
const legendOpen = ref(false) // phone-only: the map key is behind a button
const nowMs = ref(Date.now())
let map = null
let popup = null
let pollTimer = null
let cable = null

const currentDay = computed(() => days[selectedDay.value])

const preTrip = computed(() => nowMs.value < TRIP_START_MS)

const statusText = computed(() => {
  if (position.value) {
    const p = position.value
    const where = p.town || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`
    return `Position: ${where} (${brisbaneTime(p.updated_at)})`
  }
  return preTrip.value ? "Trip not started yet" : "No position reported yet"
})

function brisbaneDateToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Brisbane" }).format(new Date())
}

function brisbaneTime(iso) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Australia/Brisbane", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso))
}

function defaultDay() {
  const today = brisbaneDateToday()
  const i = itineraryData.days.findIndex((d) => d.date === today)
  if (i >= 0) return i
  return today < itineraryData.days[0].date ? 0 : itineraryData.days.length - 1
}

function roleColor(role) {
  if (/start|finish/.test(role)) return "#2f6b4f"
  if (/lunch|beers/.test(role)) return "#b3541e"
  return "#8a8172"
}

function roleLabel(role) {
  return role === "pass through" ? "" : role
}

// The day a town belongs to: first day whose end-town km covers it.
function dayForTown(km) {
  return days.find((d) => townByName[d.endTown].km_from_yarraman >= km - 1e-6) || days[days.length - 1]
}

function fmtKm(km) {
  return `${km.toFixed(1)} km`
}

// Inline SVG elevation sparkline from `fromKm` to `toKm` (both km-from-Yarraman).
// The profile is keyed by km-from-Wulkuraka: kmY = TOTAL_KM - km.
function sparklineSvg(fromKm, toKm) {
  const pts = elevationData.points
    .map((p) => ({ x: TOTAL_KM - p.km, y: p.elev_m }))
    .filter((p) => p.x >= fromKm - 1e-6 && p.x <= toKm + 1e-6)
    .sort((a, b) => a.x - b.x)
  if (pts.length < 2) return ""

  const W = 232, H = 40, PAD = 2
  const minY = Math.min(...pts.map((p) => p.y))
  const maxY = Math.max(...pts.map((p) => p.y))
  const spanY = Math.max(maxY - minY, 10)
  const sx = (x) => PAD + ((x - fromKm) / (toKm - fromKm)) * (W - 2 * PAD)
  const sy = (y) => H - PAD - ((y - minY) / spanY) * (H - 2 * PAD)
  const line = pts.map((p, i) => `${i ? "L" : "M"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join("")
  const area = `${line}L${sx(pts[pts.length - 1].x).toFixed(1)},${H}L${sx(pts[0].x).toFixed(1)},${H}Z`

  return `<svg class="bvrt-spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">
    <path d="${area}" fill="#caddd8" fill-opacity="0.9"/>
    <path d="${line}" fill="none" stroke="#4c736f" stroke-width="1.6"/>
  </svg>`
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
}

function popupHtml(town) {
  const km = town.km_from_yarraman
  const idx = stops.indexOf(town)
  const next = stops[idx + 1]
  const day = dayForTown(km)
  const endTown = townByName[day.endTown]
  const isDayEnd = town.town === day.endTown

  const nextRow = next
    ? `Next stop: ${escapeHtml(next.town)} — <b>${fmtKm(next.km_from_yarraman - km)}</b>`
    : `Next stop: <span class="bvrt-pop-here">end of trail</span>`

  const endRow = isDayEnd
    ? `End of ${day.label} (${escapeHtml(day.endTown)}): <span class="bvrt-pop-here">You’re here</span>`
    : `End of ${day.label} (${escapeHtml(day.endTown)}): <b>${fmtKm(endTown.km_from_yarraman - km)}</b>`

  const spark = isDayEnd
    ? ""
    : sparklineSvg(km, endTown.km_from_yarraman) +
      `<div class="bvrt-spark-caption">Elevation to ${escapeHtml(day.endTown)}</div>`

  return `<div>
    <div class="bvrt-pop-name">${escapeHtml(town.town)}</div>
    <div class="bvrt-pop-landmark">${escapeHtml(town.landmark || "")}</div>
    <div class="bvrt-pop-row">From Yarraman: <b>${fmtKm(km)}</b></div>
    <div class="bvrt-pop-row">${nextRow}</div>
    <div class="bvrt-pop-row">${endRow}</div>
    ${spark}
  </div>`
}

// Split the trail into one LineString per day, cutting at the vertex nearest each
// day's end town. The line runs Wulkuraka → Yarraman, i.e. the reverse of the ride,
// so the day order along the coordinate array is Sunday, Saturday, Friday.
function daySegments() {
  const coords = trailData.coordinates
  const nearestIndex = (town) => {
    let best = 0, bestD = Infinity
    coords.forEach(([lng, lat], i) => {
      const d = (lng - town.lng) ** 2 + (lat - town.lat) ** 2
      if (d < bestD) { bestD = d; best = i }
    })
    return best
  }

  // Boundaries are the end towns of every day but the last (Toogoolawah, Coominya),
  // reversed into coordinate order: Coominya is met first coming from Wulkuraka.
  const cuts = [...days]
    .slice(0, -1)
    .reverse()
    .map((d) => nearestIndex(townByName[d.endTown]))

  const bounds = [0, ...cuts, coords.length - 1]
  const features = []
  for (let i = 0; i < bounds.length - 1; i++) {
    // +1 end so consecutive segments share a vertex and leave no visual gap.
    const slice = coords.slice(bounds[i], bounds[i + 1] + 1)
    if (slice.length < 2) continue
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: slice },
      properties: { day: days.length - 1 - i }, // coordinate order is reversed
    })
  }
  return { type: "FeatureCollection", features }
}

// Data-driven paint: `selected` value for the current day's segment, `other` for the rest.
function dayPaint(selected, other) {
  return ["case", ["==", ["get", "day"], selectedDay.value], selected, other]
}

function refreshDayStyling() {
  if (!map?.getLayer("trail-line")) return
  map.setPaintProperty("trail-line", "line-color", dayPaint(SELECTED_DAY_COLOR, TRAIL_COLOR))
  map.setPaintProperty("trail-line", "line-width", dayPaint(4.4, 3.4))
}

// Mapbox parks its logo bottom-left and attribution bottom-right, both of which must
// stay visible — and both land under the trip controls. Move them to the top-right,
// which is empty, rather than pushing the controls off their corners.
function moveMapboxChromeTopRight() {
  const root = map.getContainer()
  const topRight = root.querySelector(".mapboxgl-ctrl-top-right")
  if (!topRight) return
  root
    .querySelectorAll(
      ".mapboxgl-ctrl-bottom-left > .mapboxgl-ctrl, .mapboxgl-ctrl-bottom-right > .mapboxgl-ctrl"
    )
    .forEach((el) => topRight.appendChild(el))
}

// Off-trail overnight base for all three nights.
function addBaseCamp() {
  const camp = townsData.extras?.find((e) => e.type === "accommodation")
  if (!camp) return

  const el = document.createElement("div")
  el.className = "bvrt-basecamp"
  el.setAttribute("aria-label", camp.name)
  el.innerHTML = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none"
    stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V20h13V9.5"/><path d="M10 20v-5h4v5"/></svg>`

  new mapboxgl.Marker({ element: el, anchor: "bottom" })
    .setLngLat([camp.lng, camp.lat])
    .setPopup(
      new mapboxgl.Popup({ offset: 14, maxWidth: "280px" }).setHTML(`
        <div class="bvrt-pop-name">${escapeHtml(camp.name)}</div>
        <div class="bvrt-pop-landmark">Base camp — all three nights</div>
        <div class="bvrt-pop-row">${escapeHtml(camp.address)}</div>
        <div class="bvrt-pop-row">${escapeHtml(camp.phone)}</div>`)
    )
    .addTo(map)
}

// A blue upward triangle with a white halo, sized to match the town dots.
function triangleImage() {
  const s = 44
  const c = document.createElement("canvas")
  c.width = c.height = s
  const g = c.getContext("2d")
  g.beginPath()
  g.moveTo(s / 2, 7)
  g.lineTo(s - 8, s - 9)
  g.lineTo(8, s - 9)
  g.closePath()
  g.lineJoin = "round"
  g.strokeStyle = "#ffffff"
  g.lineWidth = 6
  g.stroke()
  g.fillStyle = POSITION_COLOR
  g.fill()
  return g.getImageData(0, 0, s, s)
}

function positionFeature() {
  let coords = null
  if (position.value) coords = [position.value.lng, position.value.lat]
  else if (preTrip.value) {
    const y = townByName["Yarraman"]
    coords = [y.lng, y.lat]
  }
  return {
    type: "FeatureCollection",
    features: coords ? [{ type: "Feature", geometry: { type: "Point", coordinates: coords } }] : [],
  }
}

function refreshTriangle() {
  const src = map?.getSource("position")
  if (src) src.setData(positionFeature())
}

// Apply a record in the API's shape, from either the socket or a poll.
function applyPosition(body) {
  position.value = body?.status === "ok" ? body : null
  nowMs.value = Date.now()
  refreshTriangle()
}

async function fetchPosition() {
  nowMs.value = Date.now()
  try {
    const res = await fetch("/api/position")
    applyPosition(await res.json())
  } catch {
    // Offline on the trail — keep showing the last known record.
  }
  refreshTriangle()
}

// Live push, so a position posted from the phone lands on open maps immediately.
// The poll below stays as a fallback: mobile reception on the trail drops sockets,
// and a reconnect can miss whatever was broadcast while it was down.
function subscribeToPositions() {
  cable = createConsumer()
  cable.subscriptions.create(
    { channel: "PositionChannel" },
    {
      received: (data) => applyPosition(data),
      connected: () => fetchPosition(), // catch up on anything missed while disconnected
    }
  )
}

onMounted(() => {
  mapboxgl.accessToken = MAPBOX_TOKEN
  const coords = trailData.coordinates
  const bounds = coords.reduce(
    (b, c) => b.extend(c),
    new mapboxgl.LngLatBounds(coords[0], coords[0])
  )

  map = new mapboxgl.Map({
    container: mapEl.value,
    style: "mapbox://styles/mapbox/outdoors-v12",
    bounds,
    fitBoundsOptions: { padding: { top: 70, bottom: 30, left: 36, right: 36 } },
    attributionControl: { compact: true },
  })

  map.on("load", () => {
    moveMapboxChromeTopRight()
    map.addImage("you-triangle", triangleImage(), { pixelRatio: 2 })

    // Trail split per day so the selected day reads strongly and the rest recede.
    map.addSource("trail", { type: "geojson", data: daySegments() })
    map.addLayer({
      id: "trail-line",
      type: "line",
      source: "trail",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": dayPaint(SELECTED_DAY_COLOR, TRAIL_COLOR),
        "line-width": dayPaint(4.4, 3.4),
        "line-opacity": 1,
      },
    })

    // Position triangle sits BELOW the town dots so dots stay tappable.
    map.addSource("position", { type: "geojson", data: positionFeature() })
    map.addLayer({
      id: "position-triangle",
      type: "symbol",
      source: "position",
      layout: {
        "icon-image": "you-triangle",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        // The group is usually AT a town — at a pub, or parked at Yarraman pre-trip —
        // which put the triangle at the same point as a dot, and the dots layer paints
        // over this one (so dots stay tappable), hiding it. Sit the triangle just below
        // the point, clear of the dot's 7px radius, apex pointing up at the location.
        "icon-anchor": "top",
        "icon-offset": [0, 9],
      },
    })

    map.addSource("towns", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: stops.map((s) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [s.lng, s.lat] },
          properties: { town: s.town, type: s.type },
        })),
      },
    })
    map.addLayer({
      id: "town-dots",
      type: "circle",
      source: "towns",
      paint: {
        "circle-radius": 7,
        "circle-color": ["match", ["get", "type"],
          "trailhead", TYPE_COLORS.trailhead,
          "trailhead+pub", TYPE_COLORS["trailhead+pub"],
          "pub", TYPE_COLORS.pub,
          "park", TYPE_COLORS.park,
          "park_old_railway_stop", TYPE_COLORS.park_old_railway_stop,
          "#8a8172"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    })

    map.on("click", "town-dots", (e) => {
      const town = townByName[e.features[0].properties.town]
      if (!town) return
      popup?.remove()
      popup = new mapboxgl.Popup({ offset: 12, maxWidth: "280px" })
        .setLngLat([town.lng, town.lat])
        .setHTML(popupHtml(town))
        .addTo(map)
    })
    addBaseCamp()

    map.on("mouseenter", "town-dots", () => (map.getCanvas().style.cursor = "pointer"))
    map.on("mouseleave", "town-dots", () => (map.getCanvas().style.cursor = ""))
  })

  window.__bvrtMap = map // handy for debugging on the trail
  fetchPosition()
  subscribeToPositions()
  pollTimer = setInterval(fetchPosition, 60_000)
})

watch(selectedDay, refreshDayStyling)

onBeforeUnmount(() => {
  clearInterval(pollTimer)
  cable?.disconnect()
  popup?.remove()
  map?.remove()
})
</script>

<style>
.legend-dot {
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 9999px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(61, 52, 40, 0.12);
}

.legend-line {
  display: inline-block;
  width: 13px;
  height: 4px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-camp {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  border-radius: 4px;
  background: #4a4034;
}

/* Base camp pin — a rounded marker with a stem, distinct from the round town dots. */
.bvrt-basecamp {
  width: 24px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 4px;
  background: #4a4034;
  border: 2px solid #fff;
  border-radius: 12px 12px 12px 2px;
  transform: rotate(-45deg);
  box-shadow: 0 2px 6px rgba(61, 52, 40, 0.35);
}

.bvrt-basecamp svg {
  transform: rotate(45deg);
}
</style>
